/**
 * Map raw Prisma match rows from `/leagues/:id/schedule` to the GameResult
 * shape consumed by ScoreboardMatchRow. Keeps client rendering consistent
 * with the scoreboard screen.
 */

import type { LeagueFixture } from './leagueFixtureSections.helper';
import { GAME_STATUSES } from '../../gameResults/constants/gameResults.constants';
import { RAW_MATCH_STATUSES, UNKNOWN_TEAM_LABEL } from '../constants/leagueDetail.constants';
import { normalizeTeamKitType, type TeamKitConfig } from '../../../constants/teamKitColors';

type GameStatusValue = (typeof GAME_STATUSES)[keyof typeof GAME_STATUSES];

interface RawTeam {
  id?: string;
  name?: string;
  shortName?: string | null;
  kitPrimary?: string | null;
  kitSecondary?: string | null;
  kitType?: string | null;
}

interface RawMatch {
  id?: string;
  date?: string;
  status?: string;
  round?: string | null;
  homeScore?: number | null;
  awayScore?: number | null;
  homeTeam?: RawTeam;
  awayTeam?: RawTeam;
  /** flagcdn codes attached by the server for national-team competitions only. */
  homeCountryCode?: string | null;
  awayCountryCode?: string | null;
}

function mapStatus(raw: string | undefined): GameStatusValue {
  const s = typeof raw === 'string' ? raw.trim().toUpperCase() : '';
  if (s === RAW_MATCH_STATUSES.FINISHED) return GAME_STATUSES.COMPLETED;
  if (
    s === RAW_MATCH_STATUSES.LIVE ||
    s === RAW_MATCH_STATUSES.IN_PLAY ||
    s === RAW_MATCH_STATUSES.PAUSED
  ) {
    return GAME_STATUSES.LIVE;
  }
  return GAME_STATUSES.UPCOMING;
}

function buildKit(team: RawTeam | undefined): TeamKitConfig | undefined {
  if (!team?.kitPrimary || !team.kitSecondary) return undefined;
  if (team.kitPrimary === '' || team.kitSecondary === '') return undefined;
  return {
    primary: team.kitPrimary,
    secondary: team.kitSecondary,
    type: normalizeTeamKitType(team.kitType ?? 'plain'),
  };
}

function pickTeamLabel(team: RawTeam | undefined): string {
  const name = team?.name?.trim();
  if (name && name.length > 0) return name;
  const short = team?.shortName?.trim();
  if (short && short.length > 0) return short;
  return UNKNOWN_TEAM_LABEL;
}

export function mapScheduleMatchToGameResult(raw: unknown): LeagueFixture | null {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as RawMatch;
  if (!m.id || !m.date) return null;

  const homeKit = buildKit(m.homeTeam);
  const awayKit = buildKit(m.awayTeam);
  const homeCountryCode = typeof m.homeCountryCode === 'string' && m.homeCountryCode !== ''
    ? m.homeCountryCode
    : undefined;
  const awayCountryCode = typeof m.awayCountryCode === 'string' && m.awayCountryCode !== ''
    ? m.awayCountryCode
    : undefined;

  return {
    id: String(m.id),
    homeTeam: pickTeamLabel(m.homeTeam),
    awayTeam: pickTeamLabel(m.awayTeam),
    ...(homeKit && { homeKit }),
    ...(awayKit && { awayKit }),
    ...(homeCountryCode && { homeCountryCode }),
    ...(awayCountryCode && { awayCountryCode }),
    homeScore: Number(m.homeScore) || 0,
    awayScore: Number(m.awayScore) || 0,
    date: m.date,
    league: '',
    status: mapStatus(m.status),
    timeToStart: '',
    round: typeof m.round === 'string' ? m.round : null,
  };
}

export function mapScheduleMatches(rawMatches: unknown[]): LeagueFixture[] {
  return rawMatches
    .map(mapScheduleMatchToGameResult)
    .filter((g): g is LeagueFixture => g !== null);
}
